import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, cn } from "../lib/api";
import { Button, Card, Chip, Input, Sheet, Skeleton } from "../components/ui";
import PlayerPickerSheet from "../components/PlayerPickerSheet";
import {
  ArrowLeft, ArrowLeftRight, Undo2, Settings2, UserPlus, X, Flag, Play, Trophy, BarChart3, Search, Crown, Star,
} from "lucide-react";

const EXTRAS = [
  { key: "wd", label: "Wide" },
  { key: "nb", label: "No ball" },
  { key: "b", label: "Bye" },
  { key: "lb", label: "Leg bye" },
];

const WICKETS = [
  { key: "bowled", label: "Bowled" },
  { key: "caught", label: "Caught", fielder: true },
  { key: "lbw", label: "LBW" },
  { key: "run_out", label: "Run out", fielder: true },
  { key: "stumped", label: "Stumped", fielder: true },
  { key: "hit_wicket", label: "Hit wicket" },
  { key: "retired_hurt", label: "Retired hurt" },
];

const SLOT_LABEL = { striker: "Striker", non_striker: "Non-striker", bowler: "Bowler" };

/** Plays for both teams (uneven sides) */
export function CommonTag({ className }) {
  return (
    <span className={cn("inline-block align-middle ml-1.5 px-1.5 py-px rounded-md bg-sky-100 text-sky-700 text-[10px] font-bold uppercase tracking-wide", className)}>
      Common
    </span>
  );
}

/** Captain / vice-captain tag after a player's name */
export function RoleTag({ role, className }) {
  if (!role) return null;
  const captain = role === "C";
  return (
    <span className={cn(
      "inline-flex items-center gap-0.5 align-middle ml-1.5 px-1.5 py-px rounded-md text-[10px] font-bold",
      captain ? "bg-brand-50 text-brand-700" : "bg-amber-50 text-amber-700", className,
    )}>
      {captain ? <Crown size={10} /> : <Star size={10} />} {role}
    </span>
  );
}

/** player id -> "C" | "VC" across both teams */
const roleMap = (sc) => Object.fromEntries(sc.teams.flatMap((t) => [
  ...(t.vice_captain ? [[t.vice_captain, "VC"]] : []),
  ...(t.captain ? [[t.captain, "C"]] : []),
]));

const commonIds = (sc) => new Set(sc.teams.flatMap((t) => t.players.filter((p) => p.common).map((p) => p.id)));

export default function ScoreLivePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sc, setSc] = useState(null);
  const [members, setMembers] = useState([]);
  const [tab, setTab] = useState("live");
  const [busy, setBusy] = useState(false);
  const [extra, setExtra] = useState(null);
  const [picker, setPicker] = useState(null); // striker | non_striker | bowler
  const [wicketOpen, setWicketOpen] = useState(false);
  const [addTo, setAddTo] = useState(null); // team index
  const [editOpen, setEditOpen] = useState(false);
  const [comingIds, setComingIds] = useState(new Set());

  const scRef = useRef(null);
  const busyRef = useRef(false);
  scRef.current = sc;

  const load = useCallback(async () => {
    const { data } = await api.get(`/scores/${id}`);
    setSc(data);
    return data;
  }, [id]);

  useEffect(() => {
    Promise.all([load(), api.get("/members").then((r) => setMembers(r.data))])
      .then(([data]) => data.poll_id && api.get(`/polls/${data.poll_id}`).catch(() => null))
      .then((res) => {
        if (res) setComingIds(new Set((res.data.responses || []).filter((r) => r.status === "in").map((r) => r.member_id)));
      })
      .catch((err) => { toast.error(apiError(err)); nav("/matches?tab=scores"); });
    // eslint-disable-next-line
  }, [id]);

  // Keep spectators (and a second scorer) in sync
  useEffect(() => {
    const t = setInterval(async () => {
      const cur = scRef.current;
      if (!cur || cur.status === "completed" || busyRef.current || document.hidden) return;
      try {
        const { data } = await api.get(`/scores/${id}`);
        if (data.version > scRef.current.version) setSc(data);
      } catch { /* ignore transient errors */ }
    }, 5000);
    return () => clearInterval(t);
  }, [id]);

  const openNextMissing = (data) => {
    if (data.status !== "live") return setPicker(null);
    const inn = data.innings[data.innings.length - 1];
    if (!inn.striker) setPicker("striker");
    else if (!inn.non_striker) setPicker("non_striker");
    else if (!inn.bowler) setPicker("bowler");
    else setPicker(null);
  };

  const act = async (fn, { next = true } = {}) => {
    if (busyRef.current) return null;
    busyRef.current = true;
    setBusy(true);
    try {
      const { data } = await fn();
      setSc(data);
      if (next) openNextMissing(data);
      return data;
    } catch (err) {
      toast.error(apiError(err));
      if (err?.response?.status === 409) await load().catch(() => {});
      return null;
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  if (!sc) {
    return (
      <div className="pb-24 px-5 pt-6 space-y-3">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-36" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const inn = sc.innings[sc.innings.length - 1];
  const summ = sc.summaries[sc.summaries.length - 1];
  const batting = inn.batting;
  const fielding = 1 - batting;
  const players = Object.fromEntries(sc.teams.flatMap((t) => t.players.map((p) => [p.id, p])));
  const name = (pid) => players[pid]?.name || "—";
  const batStat = (pid) => summ.batters.find((b) => b.id === pid);
  const bowlStat = (pid) => summ.bowlers.find((b) => b.id === pid);
  const live = sc.status === "live";
  const ready = live && inn.striker && inn.non_striker && inn.bowler;

  const recordBall = (runs, wicket = null) =>
    act(() => api.post(`/scores/${id}/balls`, { runs, extra, wicket, version: sc.version }))
      .then((d) => { if (d) setExtra(null); return d; });

  const undo = () => act(() => api.post(`/scores/${id}/undo`, { version: sc.version }), { next: false });

  const setSlot = (slot, pid) => act(() => api.post(`/scores/${id}/lineup`, { [slot]: pid }));

  // Add a club member ({ member_id }) or a guest ({ name }) to the right team and put them in the slot
  const addToSlot = async (slot, who) => {
    const team = slot === "bowler" ? fielding : batting;
    const data = await act(() => api.post(`/scores/${id}/players`, { team, ...who }), { next: false });
    if (data) {
      const added = data.teams[team].players[data.teams[team].players.length - 1];
      await setSlot(slot, added.id);
    }
  };

  const addPlayers = async (team, picked) => {
    setAddTo(null);
    for (const p of picked) {
      const body = p.id ? { team, member_id: p.id } : { team, name: p.name };
      const ok = await act(() => api.post(`/scores/${id}/players`, body), { next: false });
      if (!ok) break;
    }
  };

  const removePlayer = (pid) =>
    act(() => api.delete(`/scores/${id}/players/${pid}`), { next: false });

  const setRoles = (team, { captain, vice_captain }) =>
    act(() => api.post(`/scores/${id}/roles`, { team, captain: captain || null, vice_captain: vice_captain || null }), { next: false });

  const makeCommon = (pid) =>
    act(() => api.post(`/scores/${id}/players/${pid}/common`), { next: false });

  const endInnings = () => {
    const msg = sc.innings.length === 2 ? "End the match now?" : "End this innings now?";
    if (window.confirm(msg)) act(() => api.post(`/scores/${id}/end-innings`, { version: sc.version }), { next: false });
  };

  const startSecond = () => act(() => api.post(`/scores/${id}/start-innings`, { version: sc.version }));

  const takenMemberIds = new Set(sc.teams.flatMap((t) => t.players.map((p) => p.member_id)).filter(Boolean));

  return (
    <div className="pb-28">
      <div className="flex items-center justify-between px-3 pt-4">
        <button onClick={() => nav(sc.poll_id ? `/polls/${sc.poll_id}` : "/matches?tab=scores")} className="h-10 w-10 rounded-full hover:bg-black/5 grid place-items-center" data-testid="back-btn">
          <ArrowLeft size={20} />
        </button>
        <div className="min-w-0 text-center">
          <div className="font-bold truncate">{sc.title}</div>
          <div className="text-xs text-ink-muted">{sc.overs} overs a side</div>
        </div>
        <button onClick={() => setEditOpen(true)} className="h-10 w-10 rounded-full hover:bg-black/5 grid place-items-center" data-testid="score-edit-btn" aria-label="Match settings">
          <Settings2 size={19} />
        </button>
      </div>

      <div className="px-5 mt-3 space-y-4">
        <Scoreboard sc={sc} />

        <div className="grid grid-cols-3 gap-1 p-1 bg-black/[0.04] rounded-2xl">
          {[["live", "Live"], ["card", "Scorecard"], ["teams", "Teams"]].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={cn("h-10 rounded-xl text-sm font-semibold", tab === k ? "bg-white shadow-sm text-ink" : "text-ink-muted")}
              data-testid={`score-tab-${k}`}
            >
              {l}
            </button>
          ))}
        </div>

        {tab === "live" && (
          <>
            {live && (
              <>
                <Card className="p-2">
                  <CreaseRow
                    label="Striker" pid={inn.striker} name={name} striker
                    stat={batStat(inn.striker)} onClick={() => setPicker("striker")}
                  />
                  <CreaseRow
                    label="Non-striker" pid={inn.non_striker} name={name}
                    stat={batStat(inn.non_striker)} onClick={() => setPicker("non_striker")}
                  />
                  <div className="flex items-center gap-2 px-2 pt-1 border-t border-black/[0.06] mt-1">
                    <button
                      onClick={() => setPicker("bowler")}
                      className="flex-1 flex items-center justify-between py-2.5 px-1 text-left"
                      data-testid="slot-bowler"
                    >
                      <span className="min-w-0">
                        <span className="block text-[11px] uppercase tracking-widest text-ink-muted font-semibold">Bowler</span>
                        <span className={cn("font-semibold truncate block", !inn.bowler && "text-brand")}>
                          {inn.bowler ? name(inn.bowler) : "Select bowler"}
                        </span>
                      </span>
                      {inn.bowler && bowlStat(inn.bowler) && (
                        <span className="text-sm font-bold tabular-nums">
                          {(() => { const b = bowlStat(inn.bowler); return `${b.overs}-${b.maidens}-${b.runs}-${b.wickets}`; })()}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => act(() => api.post(`/scores/${id}/swap-strike`), { next: false })}
                      className="h-9 px-3 rounded-xl bg-black/[0.04] text-xs font-semibold flex items-center gap-1"
                      disabled={busy}
                      data-testid="swap-strike-btn"
                    >
                      <ArrowLeftRight size={13} /> Swap
                    </button>
                  </div>
                </Card>

                <ThisOver balls={summ.this_over} />

                <div className="space-y-2" data-testid="keypad">
                  <div className="grid grid-cols-4 gap-2">
                    {EXTRAS.map((e) => (
                      <button
                        key={e.key}
                        onClick={() => setExtra(extra === e.key ? null : e.key)}
                        disabled={!ready || busy}
                        className={cn(
                          "h-11 rounded-2xl text-sm font-semibold border disabled:opacity-40",
                          extra === e.key ? "bg-amber-500 border-amber-500 text-white" : "bg-white border-black/10"
                        )}
                        data-testid={`extra-${e.key}`}
                      >
                        {e.label}
                      </button>
                    ))}
                  </div>
                  {extra && (
                    <div className="text-xs text-amber-700 font-semibold text-center">
                      {EXTRAS.find((e) => e.key === extra).label}: tap the runs {extra === "nb" ? "off the bat" : "taken"} (0 if none)
                    </div>
                  )}
                  <div className="grid grid-cols-4 gap-2">
                    {[0, 1, 2, 3, 4, 6].map((r) => (
                      <button
                        key={r}
                        onClick={() => recordBall(r)}
                        disabled={!ready || busy}
                        className={cn(
                          "h-16 rounded-2xl text-2xl font-extrabold btn-press disabled:opacity-40",
                          r === 4 || r === 6 ? "bg-brand text-white" : "bg-white border border-black/10"
                        )}
                        data-testid={`run-${r}`}
                      >
                        {r}
                      </button>
                    ))}
                    <button
                      onClick={() => setWicketOpen(true)}
                      disabled={!ready || busy}
                      className="h-16 rounded-2xl text-lg font-extrabold bg-red-500 text-white btn-press disabled:opacity-40 col-span-2"
                      data-testid="wicket-btn"
                    >
                      OUT
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => recordBall(5)}
                      disabled={!ready || busy}
                      className="h-11 rounded-2xl bg-white border border-black/10 text-sm font-semibold disabled:opacity-40"
                      data-testid="run-5"
                    >
                      5 runs
                    </button>
                    <button
                      onClick={undo}
                      disabled={busy}
                      className="h-11 rounded-2xl bg-ink text-white text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40"
                      data-testid="undo-btn"
                    >
                      <Undo2 size={15} /> Undo
                    </button>
                    <button
                      onClick={endInnings}
                      disabled={busy}
                      className="h-11 rounded-2xl bg-white border border-black/10 text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40"
                      data-testid="end-innings-btn"
                    >
                      <Flag size={14} /> End
                    </button>
                  </div>
                  {!ready && (
                    <button onClick={() => openNextMissing(sc)} className="w-full text-sm text-brand font-semibold py-2">
                      Select {!inn.striker || !inn.non_striker ? "batters" : "the bowler"} to start scoring
                    </button>
                  )}
                </div>
              </>
            )}

            {sc.status === "innings_break" && (
              <Card className="text-center space-y-3">
                <div className="text-sm text-ink-muted">Innings break</div>
                <div className="font-bold text-lg">
                  {sc.teams[1 - sc.innings[0].batting].name} need {sc.summaries[0].runs + 1} to win
                </div>
                <Button className="w-full" onClick={startSecond} disabled={busy} data-testid="start-second-innings-btn">
                  <Play size={16} /> Start 2nd innings
                </Button>
                <Button variant="ghost" size="sm" onClick={undo} disabled={busy}>
                  <Undo2 size={14} /> Undo last ball
                </Button>
              </Card>
            )}

            {sc.status === "completed" && (
              <Card className="text-center space-y-2">
                <Trophy className="mx-auto text-brand" size={28} />
                <div className="font-bold text-lg" data-testid="score-result">{sc.result}</div>
                {sc.poll_id && (
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <Button onClick={() => nav(`/scores/new?poll=${sc.poll_id}`)} data-testid="next-match-btn">
                      <Play size={16} /> Next match
                    </Button>
                    <Button variant="outline" onClick={() => nav(`/polls/${sc.poll_id}/report`)} data-testid="live-day-report-btn">
                      <BarChart3 size={16} /> Day report
                    </Button>
                  </div>
                )}
                <Button variant="ghost" size="sm" onClick={undo} disabled={busy}>
                  <Undo2 size={14} /> Undo last ball
                </Button>
              </Card>
            )}
          </>
        )}

        {tab === "card" && <FullScorecard sc={sc} />}

        {tab === "teams" && (
          <div className="space-y-3">
            {sc.teams.map((t, ti) => (
              <Card key={ti} className="p-3">
                <div className="flex items-center justify-between px-1 mb-2">
                  <div className="font-bold">{t.name} <span className="text-ink-muted font-normal text-sm">({t.players.length})</span></div>
                  <button
                    onClick={() => setAddTo(ti)}
                    className="text-sm font-semibold text-brand flex items-center gap-1"
                    data-testid={`team-add-player-${ti}`}
                  >
                    <UserPlus size={14} /> Add
                  </button>
                </div>
                <RolePicker team={t} onChange={(roles) => setRoles(ti, roles)} disabled={busy} />
                <div className="divide-y divide-black/[0.06]">
                  {t.players.map((p) => (
                    <div key={p.id} className="flex items-center gap-2 px-1 py-2">
                      <div className="flex-1 min-w-0 font-semibold truncate">
                        {p.name}
                        <RoleTag role={p.id === t.captain ? "C" : p.id === t.vice_captain ? "VC" : null} />
                        {p.common && <CommonTag />}
                      </div>
                      {p.guest && <Chip tone="warning">Guest</Chip>}
                      {!p.common && live && (
                        <button
                          onClick={() => window.confirm(`Make ${p.name} a common player? They'll also play for ${sc.teams[1 - ti].name}.`) && makeCommon(p.id)}
                          className="h-8 px-2 rounded-lg text-xs font-semibold text-sky-700 hover:bg-sky-50"
                          data-testid={`make-common-${p.id}`}
                        >
                          Make common
                        </button>
                      )}
                      <button
                        onClick={() => window.confirm(`Remove ${p.name}?`) && removePlayer(p.id)}
                        className="h-8 w-8 grid place-items-center rounded-lg text-ink-muted hover:bg-red-50 hover:text-red-500"
                        aria-label={`Remove ${p.name}`}
                        data-testid={`remove-player-${p.id}`}
                      >
                        <X size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
            <p className="text-xs text-ink-muted px-1">
              Players who have already batted, bowled or fielded can't be removed.
              Uneven sides? Make one player <b>common</b> — they bat and bowl for both teams.
              Removing a common player from one team keeps them in the other.
            </p>
          </div>
        )}
      </div>

      <SlotPicker
        slot={picker}
        sc={sc}
        summ={summ}
        onClose={() => setPicker(null)}
        onPick={(pid) => setSlot(picker, pid)}
        onGuest={(guestName) => addToSlot(picker, { name: guestName })}
        members={members.filter((m) => !takenMemberIds.has(m.id))}
        priorityIds={comingIds}
        onMember={(memberId) => addToSlot(picker, { member_id: memberId })}
        busy={busy}
      />

      <WicketSheet
        open={wicketOpen}
        onClose={() => setWicketOpen(false)}
        sc={sc}
        extra={extra}
        onConfirm={(runs, wicket) => { setWicketOpen(false); recordBall(runs, wicket); }}
      />

      <PlayerPickerSheet
        open={addTo !== null}
        onClose={() => setAddTo(null)}
        title={addTo !== null ? `Add to ${sc.teams[addTo].name}` : ""}
        members={members.filter((m) => !takenMemberIds.has(m.id))}
        priorityIds={comingIds}
        onDone={(picked) => addPlayers(addTo, picked)}
      />

      <EditSheet
        open={editOpen}
        onClose={() => setEditOpen(false)}
        sc={sc}
        onSave={async (body) => { if (await act(() => api.patch(`/scores/${id}`, body), { next: false })) setEditOpen(false); }}
        onDelete={async () => {
          if (!window.confirm("Delete this scorecard permanently?")) return;
          try { await api.delete(`/scores/${id}`); toast.success("Scorecard deleted"); nav("/matches?tab=scores"); }
          catch (err) { toast.error(apiError(err)); }
        }}
      />
    </div>
  );
}

export function Scoreboard({ sc }) {
  const idx = sc.summaries.length - 1;
  const s = sc.summaries[idx];
  const prev = idx > 0 ? sc.summaries[0] : null;
  return (
    <div className="rounded-3xl bg-ink text-white p-5" data-testid="scoreboard">
      <div className="flex items-center justify-between">
        <div className="text-sm text-white/70 font-semibold truncate">{sc.teams[s.batting_team].name}</div>
        {sc.status === "live" && <span className="text-[11px] font-bold uppercase tracking-widest text-red-400 flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-red-400 animate-pulse" /> Live</span>}
      </div>
      <div className="flex items-baseline gap-2 mt-1">
        <div className="text-4xl font-extrabold tabular-nums" data-testid="score-total">{s.runs}/{s.wickets}</div>
        <div className="text-white/70 font-semibold tabular-nums">({s.overs}/{sc.overs})</div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-white/70">
        <span>CRR {s.run_rate.toFixed(2)}</span>
        {sc.chase && (
          <span className="text-white font-semibold">
            Need {sc.chase.runs_needed} from {sc.chase.balls_left} ball{sc.chase.balls_left === 1 ? "" : "s"}
            {sc.chase.required_rate != null && ` • RRR ${sc.chase.required_rate.toFixed(2)}`}
          </span>
        )}
        {prev && <span>{sc.teams[prev.batting_team].name} {prev.runs}/{prev.wickets} ({prev.overs})</span>}
      </div>
      {sc.status === "completed" && sc.result && (
        <div className="mt-3 pt-3 border-t border-white/10 font-semibold">{sc.result}</div>
      )}
    </div>
  );
}

function CreaseRow({ label, pid, name, stat, striker, onClick }) {
  return (
    <button onClick={onClick} className="w-full flex items-center justify-between px-3 py-2.5 rounded-2xl hover:bg-black/[0.03] text-left" data-testid={`slot-${striker ? "striker" : "non_striker"}`}>
      <span className="min-w-0">
        <span className="block text-[11px] uppercase tracking-widest text-ink-muted font-semibold">{label}</span>
        <span className={cn("font-semibold truncate block", !pid && "text-brand")}>
          {pid ? name(pid) : `Select ${label.toLowerCase()}`}
          {pid && striker && <span className="text-brand"> *</span>}
        </span>
      </span>
      {stat && (
        <span className="text-right tabular-nums">
          <span className="text-lg font-extrabold">{stat.runs}</span>
          <span className="text-sm text-ink-muted"> ({stat.balls})</span>
        </span>
      )}
    </button>
  );
}

function ThisOver({ balls }) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="this-over">
      <span className="text-[11px] uppercase tracking-widest text-ink-muted font-semibold shrink-0">This over</span>
      {balls.length === 0 && <span className="text-sm text-ink-muted">—</span>}
      {balls.map((b, i) => (
        <span
          key={i}
          className={cn(
            "h-9 min-w-[36px] px-1.5 rounded-full grid place-items-center text-xs font-bold shrink-0",
            b.startsWith("W") ? "bg-red-500 text-white"
              : b === "4" || b === "6" ? "bg-brand text-white"
              : /[a-z]/.test(b) ? "bg-amber-100 text-amber-800"
              : "bg-black/[0.05]"
          )}
        >
          {b}
        </span>
      ))}
    </div>
  );
}

/** Captain / vice-captain dropdowns for one team (values are player ids) */
function RolePicker({ team, onChange, disabled }) {
  const pick = (role, value) => {
    const next = { captain: team.captain || "", vice_captain: team.vice_captain || "", [role]: value };
    // One person can't hold both roles: picking someone for one role clears the other
    if (value && role === "captain" && next.vice_captain === value) next.vice_captain = "";
    if (value && role === "vice_captain" && next.captain === value) next.captain = "";
    onChange(next);
  };
  const select = (role, label) => (
    <label className="min-w-0">
      <span className="text-[10px] uppercase tracking-widest text-ink-muted font-semibold block mb-1">{label}</span>
      <select
        value={team[role] || ""}
        onChange={(e) => pick(role, e.target.value)}
        disabled={disabled}
        className="w-full h-10 px-2 rounded-xl bg-black/[0.04] text-sm font-semibold outline-none"
        data-testid={`role-${role}`}
      >
        <option value="">None</option>
        {team.players.filter((p) => !p.guest).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
    </label>
  );
  return (
    <div className="grid grid-cols-2 gap-2 px-1 mb-2">
      {select("captain", "Captain")}
      {select("vice_captain", "Vice captain")}
    </div>
  );
}

function SlotPicker({ slot, sc, summ, onClose, onPick, onGuest, members, priorityIds, onMember, busy }) {
  const [guest, setGuest] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => { setGuest(""); setQ(""); }, [slot]);
  if (!slot) return null;
  const inn = sc.innings[sc.innings.length - 1];
  const isBowler = slot === "bowler";
  const team = sc.teams[isBowler ? 1 - inn.batting : inn.batting];
  const outIds = new Set(summ.batters.filter((b) => b.out && b.out !== "retired hurt").map((b) => b.id));
  const atCrease = [inn.striker, inn.non_striker];
  const lastBall = inn.balls[inn.balls.length - 1];
  const lastBowler = !inn.bowler && lastBall ? lastBall.bowler : null;

  const options = team.players.filter((p) => (isBowler ? true : !outIds.has(p.id) && !atCrease.includes(p.id)));
  const coming = (m) => priorityIds?.has(m.id);
  const others = members
    .filter((m) => m.name.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => Number(coming(b) || 0) - Number(coming(a) || 0));

  return (
    <Sheet open onClose={onClose} title={`Select ${SLOT_LABEL[slot].toLowerCase()}`}>
      <div className="space-y-4">
        {options.length === 0 ? (
          <div className="text-sm text-ink-muted">No one left in {team.name} — pick a club member or add a guest below{!isBowler && ", or end the innings"}.</div>
        ) : (
          <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto pr-1">
            {options.map((p) => {
              const bat = summ.batters.find((b) => b.id === p.id);
              const bowl = summ.bowlers.find((b) => b.id === p.id);
              const current = isBowler && inn.bowler === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => { onPick(p.id); onClose(); }}
                  disabled={busy}
                  className={cn(
                    "min-h-[48px] px-3 py-2 rounded-2xl text-left",
                    current ? "bg-brand text-white" : "bg-black/[0.04]",
                    p.id === lastBowler && "opacity-60"
                  )}
                  data-testid={`pick-${slot}-${p.id}`}
                >
                  <div className="text-sm font-semibold truncate">
                    {p.name}
                    <RoleTag role={p.id === team.captain ? "C" : p.id === team.vice_captain ? "VC" : null} className={current ? "bg-white/20 text-white" : ""} />
                    {p.common && <CommonTag className={current ? "bg-white/20 text-white" : ""} />}
                  </div>
                  <div className={cn("text-[11px]", current ? "text-white/80" : "text-ink-muted")}>
                    {isBowler
                      ? p.id === lastBowler ? "Bowled last over" : bowl ? `${bowl.overs}-${bowl.maidens}-${bowl.runs}-${bowl.wickets}` : p.guest ? "Guest" : "Yet to bowl"
                      : bat?.out === "retired hurt" ? `Retired hurt • ${bat.runs} (${bat.balls})` : p.guest ? "Guest" : "Yet to bat"}
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {members.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-1.5">
              Club members • joins {team.name}
            </div>
            {members.length > 6 && (
              <div className="relative mb-2">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search members"
                  className="w-full h-10 pl-9 pr-3 rounded-2xl bg-black/[0.04] outline-none"
                  data-testid="slot-member-search"
                />
              </div>
            )}
            {others.length === 0 ? (
              <div className="text-sm text-ink-muted py-2">No members match.</div>
            ) : (
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                {others.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => { onMember(m.id); onClose(); }}
                    disabled={busy}
                    className="h-11 px-3 rounded-2xl bg-black/[0.04] text-sm font-semibold text-left flex items-center gap-1.5"
                    data-testid={`slot-member-${m.id}`}
                  >
                    <UserPlus size={14} className="shrink-0 text-ink-muted" />
                    <span className="truncate">{m.name}</span>
                    {coming(m) && <span className="ml-auto text-[10px] font-bold text-brand shrink-0">IN</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold -mb-2">Guest player</div>
        <form
          onSubmit={(e) => { e.preventDefault(); if (guest.trim()) { onGuest(guest.trim()); onClose(); } }}
          className="flex gap-2"
        >
          <input
            value={guest}
            onChange={(e) => setGuest(e.target.value)}
            placeholder={`New guest ${isBowler ? "bowler" : "batter"} name`}
            maxLength={40}
            className="flex-1 h-11 px-3 rounded-2xl border border-black/10 outline-none focus:border-brand"
            data-testid="slot-guest-input"
          />
          <Button type="submit" size="sm" variant="outline" className="h-11" disabled={busy || !guest.trim()}>
            <UserPlus size={15} /> Add
          </Button>
        </form>
      </div>
    </Sheet>
  );
}

function WicketSheet({ open, onClose, sc, extra, onConfirm }) {
  const [kind, setKind] = useState(null);
  const [out, setOut] = useState(null);
  const [fielder, setFielder] = useState(null);
  const [runs, setRuns] = useState(0);
  const [askOut, setAskOut] = useState(false);
  useEffect(() => { if (open) { setKind(null); setOut(null); setFielder(null); setRuns(0); setAskOut(false); } }, [open]);
  if (!open) return null;

  const inn = sc.innings[sc.innings.length - 1];
  const players = Object.fromEntries(sc.teams.flatMap((t) => t.players.map((p) => [p.id, p])));
  const fielders = sc.teams[1 - inn.batting].players;
  const meta = WICKETS.find((w) => w.key === kind);
  const isRunOut = kind === "run_out";
  // A run out can dismiss either batter, so it's never defaulted to the striker
  const outId = isRunOut ? out : out || inn.striker;

  return (
    <Sheet open onClose={onClose} title="Wicket">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2">
          {WICKETS.map((w) => (
            <button
              key={w.key}
              onClick={() => {
                setKind(w.key);
                setFielder(null);
                setOut(null);
                setAskOut(w.key === "run_out");
              }}
              className={cn("h-11 rounded-2xl text-sm font-semibold", kind === w.key ? "bg-red-500 text-white" : "bg-black/[0.04]")}
              data-testid={`wicket-${w.key}`}
            >
              {w.label}
            </button>
          ))}
        </div>

        {(isRunOut || kind === "retired_hurt") && (
          <div>
            <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-1.5">Who is out?</div>
            <div className="grid grid-cols-2 gap-2">
              {[inn.striker, inn.non_striker].map((pid) => (
                <button
                  key={pid}
                  onClick={() => setOut(pid)}
                  className={cn("h-11 rounded-2xl text-sm font-semibold truncate px-2", outId === pid ? "bg-ink text-white" : "bg-black/[0.04]")}
                >
                  {players[pid]?.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {isRunOut && (
          <div>
            <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-1.5">Runs completed</div>
            <div className="grid grid-cols-4 gap-2">
              {[0, 1, 2, 3].map((r) => (
                <button key={r} onClick={() => setRuns(r)} className={cn("h-11 rounded-2xl font-bold", runs === r ? "bg-ink text-white" : "bg-black/[0.04]")}>
                  {r}
                </button>
              ))}
            </div>
          </div>
        )}

        {meta?.fielder && (
          <div>
            <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-1.5">
              {kind === "caught" ? "Caught by" : kind === "stumped" ? "Keeper" : "Fielder"} <span className="normal-case tracking-normal font-normal">(optional)</span>
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
              {fielders.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setFielder(fielder === p.id ? null : p.id)}
                  className={cn("h-10 rounded-xl text-sm font-semibold truncate px-2", fielder === p.id ? "bg-ink text-white" : "bg-black/[0.04]")}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {extra && <div className="text-xs text-amber-700 font-semibold">Recorded as a {EXTRAS.find((e) => e.key === extra).label.toLowerCase()}.</div>}

        <Button
          variant="danger"
          size="lg"
          className="w-full"
          disabled={!kind || !outId}
          onClick={() => onConfirm(isRunOut ? runs : 0, { kind, out: outId, fielder })}
          data-testid="wicket-confirm-btn"
        >
          Confirm wicket
        </Button>
      </div>

      {/* Portalled so the sheet's scroll container and slide-in transform don't clip it */}
      {askOut && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6" data-testid="runout-who-dialog">
          <div className="absolute inset-0 bg-black/40" onClick={() => setAskOut(false)} aria-hidden />
          <div className="relative w-full max-w-[360px] bg-white rounded-3xl p-5">
            <h3 className="text-lg font-bold">Run out — who is out?</h3>
            <p className="text-sm text-ink-muted mt-1 mb-4">Select the batter who was run out.</p>
            <div className="space-y-2">
              {[inn.striker, inn.non_striker].map((pid) => (
                <button
                  key={pid}
                  onClick={() => { setOut(pid); setAskOut(false); }}
                  className="w-full h-12 rounded-2xl bg-black/[0.04] hover:bg-black/[0.08] text-sm font-semibold truncate px-3 flex items-center justify-between"
                  data-testid={`runout-out-${pid === inn.striker ? "striker" : "non-striker"}`}
                >
                  <span className="truncate">{players[pid]?.name}</span>
                  <span className="text-xs font-normal text-ink-muted shrink-0 ml-2">{pid === inn.striker ? "Striker" : "Non-striker"}</span>
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </Sheet>
  );
}

function EditSheet({ open, onClose, sc, onSave, onDelete }) {
  const [title, setTitle] = useState("");
  const [overs, setOvers] = useState(20);
  const [names, setNames] = useState(["", ""]);
  useEffect(() => {
    if (open) { setTitle(sc.title); setOvers(sc.overs); setNames(sc.teams.map((t) => t.name)); }
  }, [open, sc]);

  return (
    <Sheet open={open} onClose={onClose} title="Match settings">
      <div className="space-y-4">
        <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Input label="Overs per side" type="number" min="1" max="50" value={overs} onChange={(e) => setOvers(e.target.value)} />
        <div className="grid grid-cols-2 gap-3">
          {names.map((n, i) => (
            <Input key={i} label={`Team ${i + 1}`} value={n} maxLength={40} onChange={(e) => setNames(names.map((x, j) => (j === i ? e.target.value : x)))} />
          ))}
        </div>
        <Button className="w-full" onClick={() => onSave({ title, overs: Number(overs), team_names: names })} data-testid="score-settings-save">
          Save
        </Button>
        <Button variant="ghost" className="w-full text-red-500" onClick={onDelete} data-testid="score-delete-btn">
          Delete scorecard
        </Button>
      </div>
    </Sheet>
  );
}

export function FullScorecard({ sc }) {
  const common = commonIds(sc);
  const roles = roleMap(sc);
  return (
    <div className="space-y-4" data-testid="full-scorecard">
      {sc.summaries.map((s, i) => (
        <Card key={i} className="p-0 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-black/[0.03]">
            <div className="font-bold">{sc.teams[s.batting_team].name}</div>
            <div className="font-bold tabular-nums">{s.runs}/{s.wickets} <span className="text-ink-muted font-semibold text-sm">({s.overs})</span></div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-ink-muted">
                <th className="text-left font-semibold px-4 py-2">Batter</th>
                <th className="font-semibold px-1">R</th>
                <th className="font-semibold px-1">B</th>
                <th className="font-semibold px-1">4s</th>
                <th className="font-semibold px-1">6s</th>
                <th className="font-semibold px-1 pr-4">SR</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {s.batters.map((b) => (
                <tr key={b.id} className="border-t border-black/[0.05]">
                  <td className="px-4 py-2">
                    <div className="font-semibold">{b.name}<RoleTag role={roles[b.id]} />{common.has(b.id) && <CommonTag />}</div>
                    <div className="text-[11px] text-ink-muted">{b.out || "not out"}</div>
                  </td>
                  <td className="text-center font-bold">{b.runs}</td>
                  <td className="text-center">{b.balls}</td>
                  <td className="text-center">{b.fours}</td>
                  <td className="text-center">{b.sixes}</td>
                  <td className="text-center pr-4">{b.strike_rate.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 border-t border-black/[0.05] text-sm flex justify-between">
            <span className="text-ink-muted">Extras</span>
            <span><b>{s.extras.total}</b> <span className="text-ink-muted text-xs">(wd {s.extras.wd}, nb {s.extras.nb}, b {s.extras.b}, lb {s.extras.lb})</span></span>
          </div>
          {s.yet_to_bat.length > 0 && (
            <div className="px-4 py-2 border-t border-black/[0.05] text-xs text-ink-muted">
              <span className="font-semibold">Yet to bat:</span> {s.yet_to_bat.map((p) => p.name).join(", ")}
            </div>
          )}
          {s.fall_of_wickets.length > 0 && (
            <div className="px-4 py-2 border-t border-black/[0.05] text-xs text-ink-muted">
              <span className="font-semibold">Fall of wickets:</span>{" "}
              {s.fall_of_wickets.map((f) => `${f.runs}-${f.wicket} (${sc.teams.flatMap((t) => t.players).find((p) => p.id === f.player)?.name || "?"}, ${f.overs})`).join(", ")}
            </div>
          )}
          {s.bowlers.length > 0 && (
            <table className="w-full text-sm border-t border-black/[0.08]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-ink-muted">
                  <th className="text-left font-semibold px-4 py-2">Bowler</th>
                  <th className="font-semibold px-1">O</th>
                  <th className="font-semibold px-1">M</th>
                  <th className="font-semibold px-1">R</th>
                  <th className="font-semibold px-1">W</th>
                  <th className="font-semibold px-1 pr-4">Econ</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {s.bowlers.map((b) => (
                  <tr key={b.id} className="border-t border-black/[0.05]">
                    <td className="px-4 py-2 font-semibold">{b.name}<RoleTag role={roles[b.id]} />{common.has(b.id) && <CommonTag />}</td>
                    <td className="text-center">{b.overs}</td>
                    <td className="text-center">{b.maidens}</td>
                    <td className="text-center">{b.runs}</td>
                    <td className="text-center font-bold">{b.wickets}</td>
                    <td className="text-center pr-4">{b.economy.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      ))}
    </div>
  );
}
